import { useMemo } from "react";
import { useI18n } from "@wato/i18n/react";
import { useOptionalAuth } from "../../auth/AuthProvider";
import { ChatPreview } from "../../components/ChatPreview";
import { FeatureIntro } from "../../components/FeatureIntro";
import { seedCollapsedOnce } from "../templates/guide/seed-collapsed";

/**
 * The integration / developer / growth pages that explain themselves; each has its own
 * `dashboard.developerIntro.<page>.*` texts.
 */
export type DeveloperIntroPage =
  | "apiKeys"
  | "otp"
  | "webhooks"
  | "googleSheets"
  | "docTemplates"
  | "automations"
  | "credentials"
  | "customApi"
  | "customCsv"
  | "collisions"
  | "widget"
  | "shortLinks"
  | "overview"
  | "payments"
  | "vouchers"
  | "automationSettings";

const FEATURE_KEYS: Record<DeveloperIntroPage, string> = {
  apiKeys: "developer-api-keys",
  otp: "developer-otp",
  webhooks: "developer-webhooks",
  googleSheets: "developer-google-sheets",
  docTemplates: "developer-doc-templates",
  automations: "developer-automations",
  credentials: "developer-credentials",
  customApi: "developer-custom-api",
  customCsv: "developer-custom-csv",
  collisions: "developer-collisions",
  widget: "developer-widget",
  shortLinks: "developer-short-links",
  overview: "developer-analytics-overview",
  payments: "developer-analytics-payments",
  vouchers: "developer-vouchers",
  automationSettings: "developer-automation-settings",
};

/** The expected chat: the customer's first message arriving from a widget tap / a QR scan. */
function FirstMessageChat({ page }: { readonly page: "widget" | "shortLinks" }) {
  const { t } = useI18n();
  const ns = `dashboard.developerIntro.${page}` as const;
  return (
    <ChatPreview
      businessName={t(`${ns}.businessName` as const)}
      caption={t(`${ns}.caption` as const)}
      messages={[
        { from: "customer", text: t(`${ns}.msg1` as const), time: t(`${ns}.time1` as const) },
        { from: "business", text: t(`${ns}.msg2` as const), time: t(`${ns}.time2` as const) },
      ]}
    />
  );
}

/**
 * FB2-42: "explain this feature" panel for an integration, developer or growth page: what it is,
 * when you need it and, as the steps, one sentence per step (plus the security rule where it
 * applies). Starts collapsed (these are working screens), same approach as `AnalyticsIntro`.
 * Only the website widget and the short links show a chat preview.
 */
export function DeveloperIntro({ page }: { readonly page: DeveloperIntroPage }) {
  const { t } = useI18n();
  const auth = useOptionalAuth();
  const userId = auth?.state.status === "ready" ? auth.state.me.user.id : "anon";
  const featureKey = FEATURE_KEYS[page];
  useMemo(() => seedCollapsedOnce(userId, featureKey), [userId, featureKey]);
  const ns = `dashboard.developerIntro.${page}` as const;
  return (
    <FeatureIntro
      featureKey={featureKey}
      title={t(`${ns}.title` as const)}
      what={t(`${ns}.what` as const)}
      when={t(`${ns}.when` as const)}
      steps={[t(`${ns}.step1` as const), t(`${ns}.step2` as const), t(`${ns}.step3` as const)]}
    >
      {page === "widget" || page === "shortLinks" ? <FirstMessageChat page={page} /> : null}
    </FeatureIntro>
  );
}
